import {inject, Injectable} from '@angular/core';
import {
    BehaviorSubject, finalize, Observable, of, switchMap, take, takeWhile, tap
} from 'rxjs';
import {UrlEnum} from "app/core/types/url-enum";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {UrlService} from "./url.service";
import {SMMCalendarDayWorkHours, SMMCalendarDayWorkHoursList,} from "../types/calendar.types";
import {SMMUserSelectionService} from "./smm.user.selection.service";

@Injectable({providedIn: 'root'})
export class CalendarWorkingScheduleService {
    //services
    private _httpClient = inject(HttpClient);
    private _urlService: UrlService = inject(UrlService);
    private _userSelectionService: SMMUserSelectionService = inject(SMMUserSelectionService);
    private initialized = false;

    constructor() {
        this.initWorkingHoursDaily();
    }

    //observables
    private _workingHours: BehaviorSubject<SMMCalendarDayWorkHours[] | null> = new BehaviorSubject(null);
    private _workingHoursCache: Map<string,SMMCalendarDayWorkHours[]> = new Map();
    private _busy: BehaviorSubject<boolean> = new BehaviorSubject(false);

    get busy$() {
        return this._busy.asObservable();
    }

    get workingHoursDaily$(): Observable<SMMCalendarDayWorkHours[]> {
        return this._workingHours.asObservable();
    }

    get workingHoursDailyCurrentValue(): SMMCalendarDayWorkHours[] {
        return this._workingHours.value;
    }

    public initWorkingHoursDaily() {
        if(this.initialized){
            return;
        }
        this._busy.next(true)
        this._userSelectionService.selectedMedic$.pipe(switchMap(u => {
            if(u.calendar)
            {
                if(this._workingHoursCache.has(u.calendar)){
                    return of(this._workingHoursCache.get(u.calendar))
                    .pipe(tap(workingDayHours => {
                        this._workingHours.next(workingDayHours);
                    }), finalize(() => {
                        this._busy.next(false)
                    }));
                }

                return this._httpClient.get<SMMCalendarDayWorkHours[]>(this._urlService.getUrl(UrlEnum.private_calendar_working_day_hours)
                    .replace("{calendarId}", u.calendar))
                    .pipe(tap(workingDayHours => {
                        this._workingHoursCache.set(u.calendar, workingDayHours);
                        this._workingHours.next(workingDayHours);
                    }), finalize(() => {
                        this._busy.next(false)
                    }));
            }
            else{
                // just keep taking data
                return of(null)
            }
        })).subscribe();

        this.initialized = true;
    }

    public updateWorkingDayHours(workingDayHours: SMMCalendarDayWorkHoursList){
        this._busy.next(true);
        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
        return this._httpClient.put<string>(
                this._urlService.getUrl(UrlEnum.private_calendar_working_day_hours_bulk_update),
                JSON.stringify(workingDayHours), {headers: headers}).pipe(tap(result => {
            this._workingHours.value.splice(0, this._workingHours.value.length, ...workingDayHours.calendarWorkingHoursDTOList);
            this._workingHours.next(this._workingHours.value);
        }), finalize(() => {
            this._busy.next(false)
        }));
    }
}
